import AsyncSelectComponent from '@components/inputs/asyncSelect/AsyncSelect.component';
import DatePickerComponent from '@components/inputs/datePicker/DatePicker.component';
import SelectComponent, {SelectItem} from '@components/inputs/select/Select.component';
import ModalComponent from '@components/modal/Modal.component';
import {Sizes} from '@constants/sizes.constants';
import {Button, Stack} from '@mui/material';
import {containersService} from '@store/settings/containers';
import {ServiceKindTypeEnum} from '@store/settings/serviceKinds';
import {wasteFamiliesService} from '@store/settings/wasteFamilies';
import _ from 'lodash';
import {useSnackbar} from 'notistack';
import {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useParams} from 'react-router-dom';
import {finalize, map, switchMap} from 'rxjs';
import {AuditOfferServiceFilterActionsDto} from '../../../../store/audits/audit-offer/audit-offer.model';
import {auditsOfferService} from '../../../../store/audits/audit-offer/audit-offer.services';
import AuditsToggleButton from '../components/AuditsToggleButton';

interface GroupedAction {
  containerKind: SelectItem;
  waste: SelectItem;
  servieKind: ServiceKindTypeEnum;
  startDate: Date;
  endDate: Date;
  canAsk: boolean;
  isRecurrence: boolean;
}

interface GroupActionModalProps {
  handleCloseModal: () => void;
}

const GroupActionModal = (props: GroupActionModalProps) => {
  const { handleCloseModal } = props;
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { auditId } = useParams();

  const [groupedUpdate, setGroupedUpdate] = useState<Partial<GroupedAction>>({ canAsk: true, isRecurrence: true });

  const isDisabled = !_.every((value) => _.isDate(value) || (!_.isNil(value) && value !== '')) ||
    _.every([groupedUpdate?.servieKind, groupedUpdate?.waste, groupedUpdate?.containerKind], _.isEmpty);


  const updateGroupedAction = (name: string) => (value: any) => setGroupedUpdate((state) => ({ ...state, [name]: value }));

  const handleValidate = () => {

    const data: AuditOfferServiceFilterActionsDto = {
      containerId: groupedUpdate?.containerKind?.value as number,
      wasteId: groupedUpdate?.waste?.value as number,
      serviceKindType: groupedUpdate?.servieKind as ServiceKindTypeEnum,
      validFromDate: groupedUpdate?.startDate as Date,
      validToDate: groupedUpdate?.endDate as Date,
      canBeRequested: groupedUpdate?.canAsk as boolean,
      enableSchedule: groupedUpdate?.isRecurrence as boolean,
    };

    auditsOfferService.PATCHServiceFilterActions(auditId as string, data).pipe(
      finalize(() => handleCloseModal()),
      switchMap(() => auditsOfferService.GETAuditOffersByAudit(auditId as string))
    ).subscribe({
      error: (err) => enqueueSnackbar(err.text, err.options),
      next: () => {
        enqueueSnackbar(t('audits.success.offer.price-group-action'), { variant: 'success' });
        handleCloseModal();
      },
    });
  };

  return (
    <ModalComponent
      aria-label="over-drawer"
      title={t('audits.OfferActivation.groupeActionModal.title')}
      handleClose={handleCloseModal}
      customColor='#FFFFFF'
      contentHeight='60dvh'
      contentFullWidth
      content={(
        <Stack width="100%" height={'100%'} bgcolor='#F4F6F8' justifyContent='center' alignItems='center' spacing={5}>
          <Stack spacing={3}>
            <AsyncSelectComponent
              labelWidth={Sizes.labelMatchWithWidth}
              color="secondary"
              label={t('audits.OfferFormalization.groupeActionModal.containerType')}
              placeholder={t('global.select')}
              getOptions={() => containersService.getContainers().pipe(
                map((options) => options.data.map((o) => ({ label: o.name || '-', value: o.id }))))}
              value={groupedUpdate?.containerKind}
              handleChange={updateGroupedAction('containerKind')}
            />
            <AsyncSelectComponent
              color="secondary"
              labelWidth={Sizes.labelMatchWithWidth}
              label={t('audits.OfferActivation.groupeActionModal.wasteType')}
              placeholder={t('global.select')}
              getOptions={() => wasteFamiliesService.getAllWaste().pipe(
                map((options) => options.data.map((o) => ({ label: o.name || '-', value: o.id }))))}
              value={groupedUpdate?.waste}
              handleChange={updateGroupedAction('waste')}
            />
            <SelectComponent
              color="secondary"
              labelWidth={Sizes.labelMatchWithWidth}
              label={t('audits.OfferActivation.groupeActionModal.pretation')}
              handleChange={updateGroupedAction('servieKind')}
              value={groupedUpdate?.servieKind}
              placeholder={t('global.select')}
              items={ServiceKindTypeEnum.selectItems()} />
            <DatePickerComponent
              color="secondary"
              label={t('audits.OfferActivation.groupeActionModal.startDate')}
              labelWidth={Sizes.datePickerWidth}
              placeholder={t('audits.OfferActivation.groupeActionModal.placeholderDate')}
              value={groupedUpdate?.startDate as Date}
              handleChange={updateGroupedAction('startDate')} />
            <DatePickerComponent
              color="secondary"
              label={t('audits.OfferActivation.groupeActionModal.endDate')}
              labelWidth={Sizes.datePickerWidth}
              placeholder={t('audits.OfferActivation.groupeActionModal.placeholderDate')}
              value={groupedUpdate?.endDate as Date}
              handleChange={updateGroupedAction('endDate')} />
            <AuditsToggleButton
              label={t('audits.OfferActivation.groupeActionModal.canAsk')}
              labelWidth={Sizes.datePickerWidth}
              isActive={groupedUpdate?.canAsk as boolean}
              handleToggle={(isActive) => updateGroupedAction('canAsk')(isActive)}
            />
            <AuditsToggleButton
              label={t('audits.OfferActivation.groupeActionModal.recurrence')}
              labelWidth={Sizes.datePickerWidth}
              isActive={groupedUpdate?.isRecurrence as boolean}
              handleToggle={(isActive) => updateGroupedAction('isRecurrence')(isActive)}
            />
          </Stack>
          <Button variant='contained' disabled={isDisabled} onClick={handleValidate}>
            {t('global.validate')}
          </Button>
        </Stack>
      )}
    />
  );
};

export default GroupActionModal;
